import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Upload, Wallet, Clock, ArrowLeft, FolderPlus, Folder, ChevronDown } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { Modal } from '@/shared/components/ui/Modal';
import { useUser } from '@/shared/hooks/useAuth';
import { useSalesDocs, useSalesDocMutations, useFolders } from '../hooks/useSalesDocs';
import { SalesDocFormModal } from '../components/SalesDocFormModal';
import { detectFileType, deriveFileName } from '../constants';
import { getUploaderId } from '../types';
import type { CreateSalesDocPayload, SalesDoc, UpdateSalesDocPayload } from '../types';
import { formatINR } from '@/shared/utils/currency';
import { cn, cardClassName } from '@jira-clone/ui';

function fmtDate(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function SalesDocsPage() {
  const navigate = useNavigate();
  const user = useUser();
  const role = (user?.role ?? '').toLowerCase();
  const dept = (user?.department ?? '').toLowerCase();
  const isAccountsAdmin = ['admin', 'department_admin'].includes(role) && ['accounts', 'finance'].includes(dept);
  const isPrivileged = role === 'superadmin' || isAccountsAdmin;

  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [folderFilter, setFolderFilter] = useState('');
  const { data: docs = [], isLoading, isError } = useSalesDocs({ folder: folderFilter || undefined });
  const { data: folders = [], isLoading: foldersLoading } = useFolders();
  const { createDoc, updateDoc, deleteDoc } = useSalesDocMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<SalesDoc | null>(null);
  const [confirmDoc, setConfirmDoc] = useState<SalesDoc | null>(null);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const canManage = (doc: SalesDoc) => isPrivileged || (!!user?.id && getUploaderId(doc.uploaded_by) === user.id);

  // Filter options derived from the loaded list (real/derived-real values only).
  const ownerOptions = useMemo(() => {
    const set = new Set(docs.map((d) => d.creator_name).filter(Boolean) as string[]);
    return [{ value: '', label: 'All owners' }, ...Array.from(set).sort().map((o) => ({ value: o, label: o }))];
  }, [docs]);

  const folderOptions = useMemo(() => {
    // Filter out folders that only have .folder- placeholder docs
    const validFolders = folders.filter(f => 
      docs.some(d => d.folder === f && !d.title.startsWith('.folder-'))
    );
    return [{ value: '', label: 'All folders' }, ...validFolders.map((f) => ({ value: f, label: f }))];
  }, [folders, docs]);

  const stats = useMemo(() => {
    const realDocs = docs.filter((d) => !d.title.startsWith('.folder-'));
    const total = realDocs.length;
    const totalValue = realDocs.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    const awaitingPayment = realDocs.filter((d) => d.type === 'invoice' && d.status !== 'paid').length;
    return { total, totalValue, awaitingPayment };
  }, [docs]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs
      .filter((d) => !d.title.startsWith('.folder-')) // Hide folder placeholder documents
      .filter((d) => {
        if (ownerFilter && d.creator_name !== ownerFilter) return false;
        if (folderFilter && d.folder !== folderFilter) return false;
        if (!q) return true;
        return [d.title, d.description, d.creator_name, d.folder, fmtDate(d.created_at), deriveFileName(d.url), detectFileType(d.url).label].some(
          (f) => (f ?? '').toLowerCase().includes(q),
        );
      });
  }, [docs, search, ownerFilter, folderFilter]);

  const handleCreate = () => {
    setEditDoc(null);
    setFormOpen(true);
  };
  const handleEdit = (doc: SalesDoc) => {
    setEditDoc(doc);
    setFormOpen(true);
  };

  const onCreate = (payload: CreateSalesDocPayload) =>
    createDoc.mutate(payload, {
      onSuccess: () => {
        toast.success('Document added.');
        setFormOpen(false);
      },
      onError: () => toast.error('Failed to add document.'),
    });

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name is required.');
      return;
    }
    // Create a dummy document to establish the folder, then delete it
    // This ensures the folder appears in the folders list
    createDoc.mutate(
      {
        title: `.folder-${newFolderName.trim()}`,
        description: `Folder placeholder for ${newFolderName.trim()}`,
        type: 'other',
        status: 'draft',
        folder: newFolderName.trim(),
        url: '',
      },
      {
        onSuccess: () => {
          toast.success(`Folder "${newFolderName.trim()}" created.`);
          setNewFolderName('');
          setFolderModalOpen(false);
        },
        onError: () => toast.error('Failed to create folder.'),
      }
    );
  };

  const onUpdate = (id: string, payload: UpdateSalesDocPayload) =>
    updateDoc.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Document updated.');
          setFormOpen(false);
          setEditDoc(null);
        },
        onError: () => toast.error('Failed to update document.'),
      },
    );

  const handleDelete = () => {
    if (!confirmDoc) return;
    deleteDoc.mutate(confirmDoc.id, {
      onSuccess: () => {
        toast.success('Document deleted.');
        setConfirmDoc(null);
      },
      onError: () => toast.error('Failed to delete document.'),
    });
  };

  const columns: Column<SalesDoc>[] = [
    {
      key: 'title',
      header: 'Document',
      render: (d) => {
        const ft = detectFileType(d.url);
        const Icon = ft.icon;
        return (
          <div className="flex flex-col">
            <span className="flex items-center gap-2">
              <Icon className="h-4 w-4 shrink-0 text-on-surface-variant" />
              <span className="font-medium text-on-surface">{d.title}</span>
            </span>
            {d.reference_number && (
              <span className="text-xs text-on-surface-variant mt-0.5 ml-6">{d.reference_number}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'folder',
      header: 'Folder',
      render: (d) => d.folder ? (
        <Badge tone="neutral" className="gap-1">
          <Folder className="h-3 w-3" />
          <span>{d.folder}</span>
        </Badge>
      ) : (
        <span className="text-sm text-on-surface-variant">—</span>
      ),
    },
{
        key: 'client_name' as any,
        header: 'Client',
        render: () => (
          <span className="text-sm text-on-surface-variant">—</span>
        ),
      },
    {
      key: 'amount',
      header: 'Amount',
      render: (d) => {
        if (!d.amount) return '—';
        return formatINR(d.amount);
      }
    },
    {
      key: 'status',
      header: 'Status',
      render: (d) => {
        const s = d.status || 'draft';
        const label = s.charAt(0).toUpperCase() + s.slice(1);
        const isExpired = d.valid_until && new Date(d.valid_until) < new Date() && !['finalized', 'paid', 'accepted', 'rejected'].includes(s);
        const toneMap: Record<string, any> = {
          draft: 'neutral',
          sent: 'primary',
          accepted: 'success',
          finalized: 'success',
          paid: 'success',
          rejected: 'critical',
          expired: 'critical',
          overdue: 'critical'
        };
        return (
          <div className="flex flex-col items-start gap-1">
            <Badge tone={isExpired ? 'critical' : toneMap[s] || 'neutral'}>{isExpired ? 'Expired' : label}</Badge>
            {d.valid_until && (
              <span className="text-[10px] text-on-surface-variant">Until: {fmtDate(d.valid_until)}</span>
            )}
          </div>
        );
      }
    },
    { key: 'creator_name', header: 'Uploaded By', render: (d) => d.creator_name || '—' },
    { key: 'created_at', header: 'Uploaded', render: (d) => fmtDate(d.created_at) },
  ];

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Sales Documents</h1>
            <p className="text-body-sm text-on-surface-variant">{visible.length} document{visible.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <Button onClick={handleCreate}><Upload className="h-4 w-4" /> Upload</Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
        <div className={cn(cardClassName(), 'p-5 flex items-center justify-between')}>
          <div>
            <span className="text-body-sm font-semibold text-on-surface-variant">Total Documents</span>
            <h3 className="text-display-sm font-bold text-on-surface mt-1">{stats.total}</h3>
            <span className="text-body-sm text-on-surface-variant mt-1 block">All sales documents</span>
          </div>
          <div className="rounded-full bg-primary/10 p-3.5 text-primary">
            <Upload className="h-6 w-6" />
          </div>
        </div>

        <div className={cn(cardClassName(), 'p-5 flex items-center justify-between')}>
          <div>
            <span className="text-body-sm font-semibold text-on-surface-variant">Total Value</span>
            <h3 className="text-display-sm font-bold text-on-surface mt-1">{formatINR(stats.totalValue)}</h3>
            <span className="text-body-sm text-on-surface-variant mt-1 block">Across all documents</span>
          </div>
          <div className="rounded-full bg-cyan-500/10 p-3.5 text-cyan-600">
            <Wallet className="h-6 w-6" />
          </div>
        </div>

        <div className={cn(cardClassName(), 'p-5 flex items-center justify-between')}>
          <div>
            <span className="text-body-sm font-semibold text-on-surface-variant">Awaiting Payment</span>
            <h3 className="text-display-sm font-bold text-on-surface mt-1">{stats.awaitingPayment}</h3>
            <span className="text-body-sm text-on-surface-variant mt-1 block">Unpaid invoices</span>
          </div>
          <div className="rounded-full bg-amber-500/10 p-3.5 text-amber-600">
            <Clock className="h-6 w-6" />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-72">
          <Input name="docs-search" placeholder="Search title, owner, folder…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-48">
          <Select name="docs-owner" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} options={ownerOptions} aria-label="Filter by owner" />
        </div>
        <div className="w-44">
          <Select name="docs-folder" value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)} options={folderOptions} aria-label="Filter by folder" disabled={foldersLoading} />
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-body-sm text-on-surface-variant">
          {folderFilter && (
            <Badge tone="neutral" className="gap-1">
              <Folder className="h-3 w-3" />
              <span>{folderFilter}</span>
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => { setNewFolderName(''); setFolderModalOpen(true); }}>
          <FolderPlus className="h-4 w-4" /> New Folder
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Failed to load documents" description="Check your connection or try refreshing." />
      ) : visible.length === 0 ? (
        <EmptyState
          title={search || ownerFilter ? 'No matching documents' : 'No documents yet'}
          description={search || ownerFilter ? 'Try different filters or search term.' : 'Add a document link or upload a file.'}
          action={!search && !ownerFilter ? <Button onClick={handleCreate}>New Document</Button> : undefined}
        />
      ) : (
        <div className="flex-1 overflow-y-auto -m-1 p-1">
          <Table columns={columns} data={visible} rowKey={(d) => d.id} onRowClick={(d) => navigate(`/sales-docs/${d.id}`)} empty="No documents found." />
        </div>
      )}

      <SalesDocFormModal
        open={formOpen}
        doc={editDoc}
        loading={createDoc.isPending || updateDoc.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditDoc(null);
        }}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      <ConfirmDialog
        open={!!confirmDoc}
        title="Delete document"
        message={`Delete "${confirmDoc?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteDoc.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDoc(null)}
      />

      <Modal
        open={folderModalOpen}
        onClose={() => setFolderModalOpen(false)}
        title="Create New Folder"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setFolderModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} loading={createDoc.isPending}>Create Folder</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Folder Name"
            name="folder-name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="e.g. Proposals, Contracts, Invoices"
            autoFocus
            required
          />
          <p className="text-body-sm text-on-surface-variant">
            Files can be uploaded to this folder when creating new documents.
          </p>
        </div>
      </Modal>

    </div>
  );
}
